import { api } from '@shared/api';
import { isApiProblem } from '@shared/api/problem';
import type { LoginResponse, MeResponse, MembershipView, UserView } from '@shared/api/types';
import { changeLocale, currentLocale, setMessageNumerals } from '@shared/lib/i18n/i18n';
import { isLocale } from '@shared/lib/i18n/locales';
import { DASHBOARD_PATHS, dashboardsFor, type Dashboard, type DashboardContext } from '@shared/lib/roles';
import { browserTimeZone, usePreferences } from '@shared/model/preferences';
import { sessionStore } from '@shared/session/store';
import { authApi } from '../api';

export const SELECT_ORGANIZATION_PATH = '/app/select-organization';

/** Profile preferences drive the UI: language, digits, viewer timezone. */
export async function applyUserPreferences(user: UserView): Promise<void> {
  const prefs = usePreferences.getState();
  const numerals = user.numerals === 'arab' ? 'arab' : 'latn';
  prefs.setNumerals(numerals);
  await setMessageNumerals(numerals);
  prefs.setTimeZone(user.timezone || browserTimeZone());
  if (isLocale(user.preferred_language) && user.preferred_language !== currentLocale()) {
    await changeLocale(user.preferred_language);
  }
}

export async function loadMe(): Promise<MeResponse | null> {
  try {
    const me = await authApi.me();
    sessionStore.get().applyMe(me);
    return me;
  } catch (error) {
    // Overdue MFA enrolment blocks org-scoped endpoints. Keep the session (tokens + user from the
    // login/refresh payload) so the user can still reach /app/account/security and enrol.
    if (isApiProblem(error) && error.code === 'MFA_ENROLLMENT_REQUIRED') {
      sessionStore.get().setMfaEnrollmentRequired(true);
      return null;
    }
    throw error;
  }
}

/** Store tokens from login / accept-invite, enrich with /users/me, apply preferences. */
export async function completeSignIn(response: LoginResponse): Promise<MeResponse | null> {
  sessionStore.get().applyLogin(response);
  const me = await loadMe();
  await applyUserPreferences(me ?? response.user);
  return me;
}

let bootPromise: Promise<void> | null = null;

/**
 * Silent session restore on app boot: the refresh cookie is httpOnly, the access token lives only
 * in memory, so every page load starts with POST /auth/refresh (skipped when no tb_csrf cookie).
 */
export function bootstrapSession(): Promise<void> {
  bootPromise ??= (async () => {
    try {
      const restored = await api.refreshSession();
      if (!restored) {
        sessionStore.get().markAnonymous();
        return;
      }
      const me = await loadMe();
      const user = me ?? sessionStore.get().user;
      if (user) await applyUserPreferences(user);
    } catch {
      sessionStore.get().markAnonymous();
    }
  })();
  return bootPromise;
}

export async function signOut(): Promise<void> {
  try {
    await authApi.logout();
  } catch {
    /* the local session is cleared regardless */
  }
  sessionStore.get().clear();
  usePreferences.getState().setTimeZone(browserTimeZone());
}

export function activeMembership(memberships: readonly MembershipView[], organizationId: string | null): MembershipView | undefined {
  return memberships.find((m) => m.organization_id === organizationId);
}

/** Roles in the active organization, preferring the server-resolved list from /users/me. */
export function dashboardContext(state: {
  me: MeResponse | null;
  user: UserView | null;
  memberships: readonly MembershipView[];
  activeOrganizationId: string | null;
}): DashboardContext {
  const membership = activeMembership(state.memberships, state.activeOrganizationId);
  const roles = state.me?.active_roles.length ? state.me.active_roles : (membership?.roles ?? []);
  return {
    roles,
    isSuperAdmin: Boolean(state.user?.is_super_admin),
    organizationKind: membership?.organization_kind ?? null,
  };
}

export function availableDashboards(): Dashboard[] {
  return dashboardsFor(dashboardContext(sessionStore.get()));
}

/** Where to land after sign-in (brief §4 roles → dashboards). */
export function homePath(): string {
  const state = sessionStore.get();
  const activeMemberships = state.memberships.filter((m) => m.status === 'active');
  if (!state.activeOrganizationId && activeMemberships.length > 1) return SELECT_ORGANIZATION_PATH;
  const first = availableDashboards()[0];
  return first ? DASHBOARD_PATHS[first] : SELECT_ORGANIZATION_PATH;
}

/** POST /users/me/active-organization returns a new access token scoped to that org. */
export async function switchOrganization(organizationId: string): Promise<void> {
  const store = sessionStore.get();
  const response = await authApi.switchOrganization(organizationId);
  store.setAccessToken(response.access_token);
  store.setActiveOrganization(organizationId);
  await loadMe();
}

/** Only relative in-app paths are accepted as post-login redirects (no open redirect). */
export function safeRedirect(target: unknown): string | null {
  if (typeof target !== 'string') return null;
  if (!target.startsWith('/app') || target.startsWith('//')) return null;
  return target;
}
